import type React from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import type { SalesforceTask } from '../../../../../domain/salesforceActivity';
import { SalesforceActivityTaskSubtype } from '../../../../../domain/salesforceActivity';
import { useFormatRelativeTime } from '../../../../../hooks/useFormatRelativeTime';

import {
  StyledActivityCard,
  StyledActivityCardBody,
  StyledActivityCardHeader,
  StyledActivityCardInfo,
  StyledActivityCardInfoText,
  StyledActivityDescription,
  StyledActivityStatus,
  StyledActivityTitle,
} from './styles';

const DESCRIPTION_THRESHOLD = 100;

const ICONS_BY_SUBTYPE = {
  [SalesforceActivityTaskSubtype.Email]: 'email',
  [SalesforceActivityTaskSubtype.Task]: 'list-3-rows',
  [SalesforceActivityTaskSubtype.Call]: 'phone-outlined',
  default: 'daily-ranking',
};

type Props = {
  task: SalesforceTask;
};

export const ActivityCard: React.FC<Props> = ({ task }) => {
  const activityTime = useFormatRelativeTime(task.activityDate || '');

  const iconName = ICONS_BY_SUBTYPE[task.taskSubtype] ?? ICONS_BY_SUBTYPE.default;

  const description = (task.description || '').replace(/\s+(\n|\\n)+\s+/gim, '\n');
  const descriptionParts = (description || '').split('\n');

  return (
    <StyledActivityCard data-automation="SalesforceActivityCard">
      <StyledActivityCardHeader>
        <StyledActivityCardInfo>
          <SWReactIcons iconName={iconName} size="xs" iconColor={colorsPalettes.bluegrey[500]} />
          <StyledActivityCardInfoText>
            {task.taskSubtype} - {task.status}
          </StyledActivityCardInfoText>
        </StyledActivityCardInfo>
        {activityTime ? <StyledActivityStatus>{activityTime}</StyledActivityStatus> : null}
      </StyledActivityCardHeader>
      <StyledActivityCardBody>
        <StyledActivityTitle>{task.subject}</StyledActivityTitle>
        {description &&
        description.length < DESCRIPTION_THRESHOLD &&
        task.taskSubtype !== SalesforceActivityTaskSubtype.Email ? (
          <StyledActivityDescription>
            {descriptionParts.map((descriptionPart, index) => (
              <span key={index}>
                {descriptionPart}
                {index + 1 < descriptionParts.length ? <br /> : null}
              </span>
            ))}
          </StyledActivityDescription>
        ) : null}
      </StyledActivityCardBody>
    </StyledActivityCard>
  );
};
